import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { MessageSquare, Send, Reply, Trash2, Loader2 } from 'lucide-react';
import { useAuthStore } from '../../state/authStore';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/comments`;

function CommentItem({ comment, onReply, onDelete, userId, depth = 0 }) {
  const isOwn = comment.user_id === userId;
  const initial = (comment.user_name || '?')[0].toUpperCase();

  return (
    <div className={depth > 0 ? 'ml-6 border-l border-border/40 pl-3' : ''}>
      <div className="group flex gap-2 py-2">
        <div className="w-6 h-6 rounded-full bg-secondary flex items-center justify-center text-[10px] font-semibold shrink-0 mt-0.5">
          {initial}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium">{comment.user_name || comment.user_id}</span>
            <span className="text-[10px] text-muted-foreground">
              {new Date(comment.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
          <p className="text-xs text-foreground/90 mt-0.5 whitespace-pre-wrap">{comment.content}</p>
          <div className="flex gap-2 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button onClick={() => onReply(comment.id)} className="text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-0.5">
              <Reply className="w-2.5 h-2.5" /> Reply
            </button>
            {isOwn && (
              <button onClick={() => onDelete(comment.id)} className="text-[10px] text-muted-foreground hover:text-red-500 flex items-center gap-0.5">
                <Trash2 className="w-2.5 h-2.5" /> Delete
              </button>
            )}
          </div>
        </div>
      </div>
      {(comment.replies || []).map(reply => (
        <CommentItem
          key={reply.id}
          comment={reply}
          onReply={onReply}
          onDelete={onDelete}
          userId={userId}
          depth={depth + 1}
        />
      ))}
    </div>
  );
}

export default function CommentThread({ entityType, entityId }) {
  const user = useAuthStore(s => s.user);
  const userId = user?.id;
  const userName = user?.name || user?.email;

  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [posting, setPosting] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const loadComments = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/${entityType}/${entityId}`);
      setComments(data || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, [entityType, entityId]);

  useEffect(() => {
    if (expanded) loadComments();
  }, [expanded, loadComments]);

  const handlePost = async () => {
    if (!text.trim() || !userId) return;
    setPosting(true);
    try {
      await axios.post(API, {
        entity_type: entityType,
        entity_id: entityId,
        user_id: userId,
        user_name: userName || '',
        content: text.trim(),
        parent_id: replyTo || undefined,
      });
      setText('');
      setReplyTo(null);
      loadComments();
    } catch { /* ignore */ }
    setPosting(false);
  };

  const handleDelete = async (commentId) => {
    try {
      await axios.delete(`${API}/${commentId}`, { params: { user_id: userId } });
      loadComments();
    } catch { /* ignore */ }
  };

  const totalCount = comments.reduce((acc, c) => acc + 1 + (c.replies?.length || 0), 0);

  return (
    <div className="border-t border-border/30 mt-3">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 py-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
      >
        <MessageSquare className="w-3 h-3" />
        {totalCount > 0 ? `${totalCount} comment${totalCount !== 1 ? 's' : ''}` : 'Add comment'}
      </button>

      {expanded && (
        <div className="space-y-1 pb-2">
          {loading && (
            <div className="flex items-center gap-1 py-2 text-muted-foreground">
              <Loader2 className="w-3 h-3 animate-spin" /> <span className="text-[11px]">Loading…</span>
            </div>
          )}

          {comments.map(c => (
            <CommentItem
              key={c.id}
              comment={c}
              onReply={setReplyTo}
              onDelete={handleDelete}
              userId={userId}
            />
          ))}

          {userId && (
            <div className="flex gap-2 mt-2">
              <input
                value={text}
                onChange={e => setText(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handlePost()}
                placeholder={replyTo ? 'Write a reply…' : 'Add a comment…'}
                className="flex-1 text-xs px-2 py-1.5 rounded-md border border-border bg-background focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
              <button
                onClick={handlePost}
                disabled={posting || !text.trim()}
                className="px-2 py-1.5 rounded-md bg-emerald-600 text-white text-xs disabled:opacity-50 hover:bg-emerald-700 transition-colors"
              >
                {posting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
              </button>
            </div>
          )}
          {replyTo && (
            <button onClick={() => setReplyTo(null)} className="text-[10px] text-muted-foreground hover:text-foreground">
              Cancel reply
            </button>
          )}
        </div>
      )}
    </div>
  );
}
